import React, { useState } from 'react';
import {
    combineStatGroups,
    formatStatLine,
    STAT_MODE_ACTUAL,
} from '../../utils/statLineFormat';
import './CollapsibleScoreBreakdown.css';

/**
 * Collapsible Score Breakdown Component
 * 
 * Defaults to OPEN when screen loads
 * When CLOSED: Only shows HUGE colored triangles (yellow for stats, green for gear)
 * When OPEN: Shows stat line (bold/bigger), points (bold/bigger), then indented breakdown with bullets
 */
function CollapsibleScoreBreakdown({ breakdown }) {
    const [expandedSections, setExpandedSections] = useState({
        stats: true,      // Default to OPEN
        gearAdj: true     // Default to OPEN
    });
    
    if (!breakdown) {
        return (
            <div className="score-breakdown-container">
                <div className="empty-state" style={{
                    padding: '40px 20px',
                    textAlign: 'center',
                    color: '#7f8c8d',
                    fontStyle: 'italic'
                }}>
                    No scoring data available yet
                </div>
            </div>
        );
    }
    
    const toggleSection = (section) => {
        setExpandedSections(prev => ({
            ...prev,
            [section]: !prev[section]
        }));
    };

    // STAT-LINE FORMATTING IS SHARED WITH THE GEAR TAB'S PROJECTION VIEW.
    // It used to live inline here. The Gear tab must render projected stats in
    // exactly this format, and two copies of the ordering/labelling rules would
    // inevitably drift, so the logic now lives in `utils/statLineFormat.js` and
    // both screens call it. Behaviour is unchanged, including the deliberate
    // "throw loudly on NaN" policy.
    const baseStats = combineStatGroups({
        passing: breakdown.passing?.stat_line,
        rushing: breakdown.rushing?.stat_line,
        receiving: breakdown.receiving?.stat_line
    });

    const modifiedStats = combineStatGroups({
        passing: breakdown.passing?.stat_line_modified,
        rushing: breakdown.rushing?.stat_line_modified,
        receiving: breakdown.receiving?.stat_line_modified
    });

    // ⚠️ ACTUAL MODE — STATED EXPLICITLY, NOT LEFT TO THE DEFAULT.
    //
    // `stat_line_modified` is NOT integral: `calculation_receipt.py` stores
    // `float_value * multiplier`, so a geared-up line really does carry values
    // like `carries: 17.85`. These are ACTUAL results, so they must floor to
    // "17 CAR" exactly as this tab has always rendered them — printing
    // "17.85 CAR" would claim a fractional carry occurred.
    const baseStatLine = formatStatLine(baseStats, STAT_MODE_ACTUAL);
    const modifiedStatLine = formatStatLine(modifiedStats, STAT_MODE_ACTUAL);
    
    const baseScore = breakdown.base_score || 0;
    const gearBonus = breakdown.gear_bonus || 0;
    const totalScore = breakdown.total_score || 0;
    
    return (
        <div className="collapsible-score-breakdown">
            {/* Summary bar */}
            <div className="breakdown-summary">
                <span className="summary-item">
                    <strong>Base:</strong> {baseScore.toFixed(2)}pts
                </span>
                <span className="summary-separator">|</span>
                <span className="summary-item">
                    <strong>Gear:</strong> +{gearBonus.toFixed(2)}pts
                </span>
                <span className="summary-separator">|</span>
                <span className="summary-item">
                    <strong>Total:</strong> {totalScore.toFixed(2)}pts
                </span>
            </div>
            
            <div className="breakdown-sections">
                
                {/* STATS SECTION - YELLOW/ORANGE TRIANGLE */}
                <div className="score-section" style={{marginBottom: '10px'}}>
                    {/* When closed: ONLY the HUGE YELLOW triangle is visible */}
                    {!expandedSections.stats && (
                        <div onClick={() => toggleSection('stats')} style={{
                            cursor: 'pointer',
                            padding: '8px 0'
                        }}>
                            <span style={{fontSize: '60px', color: '#FFB800', fontWeight: 'bold', lineHeight: '1'}}>
                                ▶
                            </span>
                        </div>
                    )}
                    
                    {/* When open: Show everything */}
                    {expandedSections.stats && (
                        <div style={{display: 'flex', alignItems: 'flex-start'}}>
                            {/* HUGE YELLOW TRIANGLE */}
                            <div onClick={() => toggleSection('stats')} style={{
                                width: '70px',
                                cursor: 'pointer',
                                flexShrink: 0
                            }}>
                                <span style={{fontSize: '60px', color: '#FFB800', fontWeight: 'bold', lineHeight: '1'}}>
                                    ▼
                                </span>
                            </div>
                            
                            {/* Content to the right - push down to align with triangle */}
                            <div style={{flex: 1, paddingLeft: '8px', paddingTop: '12px'}}>
                                {/* Header */}
                                <div style={{fontWeight: 'bold', fontSize: '14px', color: '#333', marginBottom: '4px', lineHeight: '1'}}>
                                    Stats:
                                </div>
                                
                                {/* Stat line - BOLD and BIGGER */}
                                <div style={{
                                    fontSize: '13px',
                                    fontWeight: 'bold',
                                    marginBottom: '4px',
                                    color: '#333'
                                }}>
                                    {baseStatLine || '(no stats)'}
                                </div>
                                
                                {/* Points total - BOLD and BIGGER */}
                                <div style={{
                                    fontSize: '13px',
                                    fontWeight: 'bold',
                                    marginBottom: '4px',
                                    color: '#333'
                                }}>
                                    Points: {baseScore.toFixed(2)}pts
                                </div>
                                
                                {/* Breakdown - indented with bullets, smaller */}
                                <div style={{paddingLeft: '16px', fontSize: '11px', color: '#555'}}>
                                    <div>• Passing: {(breakdown.passing?.total_points || 0).toFixed(2)}pts</div>
                                    <div>• Rushing: {(breakdown.rushing?.total_points || 0).toFixed(2)}pts</div>
                                    <div>• Receiving: {(breakdown.receiving?.total_points || 0).toFixed(2)}pts</div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
                
                {/* GEAR ADJ SECTION - GREEN TRIANGLE */}
                <div className="score-section">
                    {/* When closed: ONLY the HUGE GREEN triangle is visible */}
                    {!expandedSections.gearAdj && (
                        <div onClick={() => toggleSection('gearAdj')} style={{
                            cursor: 'pointer',
                            padding: '8px 0'
                        }}>
                            <span style={{fontSize: '60px', color: '#7CB342', fontWeight: 'bold', lineHeight: '1'}}>
                                ▶
                            </span>
                        </div>
                    )}
                    
                    {/* When open: Show everything */}
                    {expandedSections.gearAdj && (
                        <div style={{display: 'flex', alignItems: 'flex-start'}}>
                            {/* HUGE GREEN TRIANGLE */}
                            <div onClick={() => toggleSection('gearAdj')} style={{
                                width: '70px',
                                cursor: 'pointer',
                                flexShrink: 0
                            }}>
                                <span style={{fontSize: '60px', color: '#7CB342', fontWeight: 'bold', lineHeight: '1'}}>
                                    ▼
                                </span>
                            </div>
                            
                            {/* Content to the right - push down to align with triangle */}
                            <div style={{flex: 1, paddingLeft: '8px', paddingTop: '12px'}}>
                                {/* Header */}
                                <div style={{fontWeight: 'bold', fontSize: '14px', color: '#333', marginBottom: '4px', lineHeight: '1'}}>
                                    Gear Adj:
                                </div>
                                
                                {/* Stat line - BOLD and BIGGER */}
                                <div style={{
                                    fontSize: '13px',
                                    fontWeight: 'bold',
                                    marginBottom: '4px',
                                    color: '#333'
                                }}>
                                    {modifiedStatLine || '(no stats)'}
                                </div>
                                
                                {/* Points total - BOLD and BIGGER */}
                                <div style={{
                                    fontSize: '13px',
                                    fontWeight: 'bold',
                                    marginBottom: '4px',
                                    color: '#333'
                                }}>
                                    Points: {gearBonus.toFixed(2)}pts
                                </div>
                                
                                {/* Gear items - show name with description below, NOT technical impacts */}
                                {breakdown.gear && breakdown.gear.items && breakdown.gear.items.length > 0 && (
                                    <div style={{paddingLeft: '16px', fontSize: '11px', color: '#555'}}>
                                        {breakdown.gear.items.map((item, idx) => (
                                            <div key={idx} style={{marginBottom: '6px'}}>
                                                {/* Item name with bullet - bold */}
                                                <div style={{fontWeight: 'bold', color: '#333'}}>• {item.name}</div>
                                                {/* Description below - indented, italic */}
                                                {item.description && (
                                                    <div style={{fontSize: '10px', paddingLeft: '12px', lineHeight: '1.4', fontStyle: 'italic'}}>
                                                        {item.description}
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default CollapsibleScoreBreakdown;